import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonaLayout, type PersonaConfig } from './PersonaLayout';

const TITLE = "WhatsApp Alerts for PR & Comms Teams — Real-Time X";
const DESCRIPTION = "In PR, minutes matter. Follow journalists, executives, and rival brands on X and get their posts on WhatsApp instantly — respond before the story sets.";
const CANONICAL = 'https://wallawhats.com/for-pr';

const CONFIG: PersonaConfig = {
  eyebrow: 'For PR & comms teams',
  h1a: 'In PR, minutes decide',
  h1b: 'the story.',
  heroSub:
    "A reporter's post, an exec's slip, a rival's move — the window to respond is minutes. WallaWhats watches the accounts that matter and pings your on-call phone the second they post.",
  freeTierNote: 'Free tier: 2 monitored accounts. Multi-recipient WhatsApp delivery on every plan.',
  problemHeading: 'Why the morning brief is already too late',
  problems: [
    { t: 'The news cycle is instant', d: 'By the time a clip lands in a morning brief, the narrative has already formed on X. You needed to know at 7:58, not 9:00.' },
    { t: 'Your team is on-call, not on-screen', d: 'Nights, weekends, travel. No one is staring at a dashboard when the reporter posts the question you must answer.' },
    { t: 'Monitoring tools batch', d: 'Media-monitoring suites summarize hourly — great for reports, useless when a viral post needs a response in 20 minutes.' },
    { t: 'Everyone needs to know at once', d: 'Your CEO, agency, and legal all need the same alert simultaneously — not a game of forwarded screenshots.' },
  ],
  useCasesHeading: 'What comms teams monitor',
  useCases: [
    { icon: '📰', title: 'Beat reporters', desc: 'The journalists who cover you. Know the moment they post a question, story, or request for comment.' },
    { icon: '🎙️', title: 'Your executives', desc: "Catch an exec's post that needs framing — or damage control — before it spreads." },
    { icon: '🥷', title: 'Competitor brands', desc: 'Rival announcements and missteps: opportunities to position, and risks to prepare for.' },
    { icon: '🚨', title: 'Crisis-watch handles', desc: 'Accounts likely to break bad news about your company or sector. Early warning, in real time.' },
    { icon: '🏛️', title: 'Official & regulatory', desc: 'Agencies and officials whose posts affect you. Be ready before the calls come.' },
    { icon: '📣', title: 'Brand-watch lists', desc: 'A curated list of accounts that frequently mention your brand — account-tracking that doubles as mention-watch.' },
  ],
  sampleHandle: '@TechReporter',
  sampleText: 'Hearing [Company] had an incident affecting customers. Reached out for comment — anyone with details, DMs open.',
  sampleFooter: 'The on-call comms lead gets it instantly — and the CEO, agency, and legal get the same alert in the same second.',
  pricingHeading: 'Plans for comms teams',
  pricingCopy:
    'Most teams run on Business ($29/mo, 20 accounts) — reporters, execs, competitors, and crisis handles — with multi-recipient WhatsApp delivery. Enterprise ($99/mo, 50) covers larger media lists.',
  faq: [
    { q: 'Can the whole team + agency get alerts?', a: 'Yes — route one account\'s alerts to multiple WhatsApp numbers: comms lead, exec, agency, legal, all at once.' },
    { q: 'Slack or Teams instead of WhatsApp?', a: 'WhatsApp first — it cuts through after-hours when it matters most. Slack delivery is on the roadmap.' },
    { q: 'Keyword/mention monitoring?', a: 'Account-level today. A curated list of accounts that mention your brand works as a mention-watch; keyword monitoring is on the roadmap.' },
    { q: 'How fast are alerts?', a: "Seconds after the post — filtered-stream firehose. Velocity caps digest storms so a viral thread doesn't flood the team." },
  ],
  ctaH2: 'Respond before the story sets.',
  ctaSub: 'Put your reporters, execs, and rivals on watch in two minutes. Free to start.',
};

export function ForPRPage() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.title = TITLE;
    document.querySelector('meta[name="description"]')?.setAttribute('content', DESCRIPTION);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', CANONICAL);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', TITLE);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', DESCRIPTION);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', CANONICAL);
  }, [i18n.language]);
  return <PersonaLayout config={CONFIG} />;
}
